// The Products (/products/:productId?) and CategoryProducts
// (/:rootCategory/:subCategory?) routes both render the same Products.vue
// component; Products is reached purely for SEO-friendly canonical product
// URLs. This module lets router.js, App.vue and Products.vue agree, in one
// place, on whether a given navigation is "the same listing with a modal
// toggled" vs. a real navigation to different content.

const PRODUCTS_ROUTE_NAMES = new Set(["Products", "CategoryProducts"]);

function isProductsRoute(name) {
  return PRODUCTS_ROUTE_NAMES.has(name);
}

function listingPathFor(route) {
  return route.name === "CategoryProducts" ? route.path : "/products";
}

function withoutProductId(query) {
  const rest = { ...query };
  delete rest.productId;
  delete rest.productid;
  return rest;
}

// The path of whichever listing (a category, or the plain /products page)
// is currently/was most recently being browsed — independent of whether a
// product modal (productId) is layered on top of it.
let activeListingPath = "/products";

// Set once per navigation by router.js's beforeEach; read by scrollBehavior
// (same navigation) and by Products.vue's $route.params watcher (the
// navigation that just landed).
let pendingNavigationIsModalToggle = false;

function isModalToggleNavigation(from, to) {
  if (!isProductsRoute(from.name) || !isProductsRoute(to.name)) return false;

  if (
    JSON.stringify(withoutProductId(to.query)) !==
    JSON.stringify(withoutProductId(from.query))
  ) {
    return false;
  }

  const fromHasProduct = !!from.params.productId;
  const toHasProduct = !!to.params.productId;

  if (from.name === to.name) {
    if (
      from.name === "CategoryProducts" &&
      (from.params.rootCategory !== to.params.rootCategory ||
        from.params.subCategory !== to.params.subCategory)
    ) {
      return false; // navigating to a different category, not a modal toggle
    }
    return (
      fromHasProduct !== toHasProduct ||
      from.params.productId !== to.params.productId
    );
  }

  // A cross-name transition can only be CategoryProducts <-> Products.
  if (from.name === "CategoryProducts" && to.name === "Products") {
    return toHasProduct; // opening the modal — same underlying listing
  }
  if (from.name === "Products" && to.name === "CategoryProducts") {
    // Only a toggle if we're returning to the listing that was active
    // before the modal opened (guaranteed, since closeModal always
    // navigates back to its stored previous path).
    return fromHasProduct && to.path === activeListingPath;
  }

  return false;
}

// Called once, from router.js's beforeEach, for every navigation.
export function resolveProductsNavigation(from, to) {
  if (isProductsRoute(to.name)) {
    pendingNavigationIsModalToggle = isModalToggleNavigation(from, to);
    if (!pendingNavigationIsModalToggle) {
      activeListingPath = listingPathFor(to);
    }
  } else {
    pendingNavigationIsModalToggle = false;
  }
}

export function isPendingNavigationModalToggle() {
  return pendingNavigationIsModalToggle;
}

export function getActiveListingPath() {
  return activeListingPath;
}
